import * as SecureStore from "@-/com.npmjs.expo-secure-store";
import onetime from "@-/com.npmjs.onetime";
import { createMMKV } from "@-/com.npmjs.react-native-mmkv";
import { Platform } from "@-/com.npmjs.react-native";
import { getDeviceAccount } from "@-/dev.improve.haniverse.lib.device-settings";
import { logger } from "@-/dev.improve.haniverse.lib.logger";

export const INSTALL_MARKER_KEY = "installed";

export const getInstallMarkerStorage = onetime(() => createMMKV({ id: "install-marker" }));

// `@better-auth/expo`'s signed-out value (what its `clearSessionCache` writes) and the prefix it
// puts on a base key whose value is split across `<key>.0..N` chunks.
const SIGNED_OUT_VALUE = "{}";
const CHUNK_MARKER = "ba-chunks:";

/**
 * iOS keeps an app's Keychain items after the app is deleted, so a reinstall would come back
 * signed in to the previous session (better-auth/expo stores it in SecureStore = the Keychain).
 * A fresh install must start signed out: the sandbox (study databases, device settings) is gone,
 * and the session would otherwise outlive everything it belongs with.
 *
 * The marker lives in its own MMKV instance, inside the app sandbox, so deleting the app deletes
 * it too. A missing marker therefore means a fresh install, except for installs from before the
 * marker existed: those are recognized by their device account (every signed-in native install
 * provisions one) and stamped without clearing anything.
 *
 * Must run before the auth client is constructed, since `expoClient` primes the session
 * synchronously from `sessionKeys` while it is being built.
 */
export function clearAuthSessionOnFreshInstall(sessionKeys: readonly string[]): void {
  if (Platform.OS === "web") {
    return;
  }
  const installMarker = getInstallMarkerStorage();
  if (installMarker.contains(INSTALL_MARKER_KEY)) {
    return;
  }
  if (getDeviceAccount() == null) {
    for (const key of sessionKeys) {
      clearAuthSessionItem(key);
    }
  }
  installMarker.set(INSTALL_MARKER_KEY, true);
}

function clearAuthSessionItem(key: string): void {
  const stored = SecureStore.getItem(key);
  if (stored == null) {
    return;
  }
  // Synchronous so the signed-out value is in place before the client primes its session.
  SecureStore.setItem(key, SIGNED_OUT_VALUE);
  if (!stored.startsWith(CHUNK_MARKER)) {
    return;
  }
  const chunkCount = Number(stored.slice(CHUNK_MARKER.length));
  // The chunks are unreachable once the base key no longer names them, so their removal can
  // finish in the background.
  for (let index = 0; index < chunkCount; index++) {
    SecureStore.deleteItemAsync(`${key}.${index}`).catch((error: unknown) => {
      logger(import.meta.location).warn(
        `failed to delete stale auth chunk "${key}.${index}"`,
        error,
      );
    });
  }
}
